import React, { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { videos } from "@/data/videos";

const youtubeId = (url) => {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|watch\?v=|v\/|shorts\/))([\w-]+)/);
  return m ? m[1] : null;
};

const vimeoId = (url) => {
  const m = url.match(/vimeo\.com\/(\d+)/);
  return m ? m[1] : null;
};

function VideoCard({ v, idx, lang }) {
  const [playing, setPlaying] = useState(false);
  const title = v[`title_${lang}`] || v.title_en || "";

  const renderPlayer = () => {
    if (v.type === "youtube") {
      const id = youtubeId(v.url);
      return (
        <iframe
          src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      );
    }
    if (v.type === "vimeo") {
      const id = vimeoId(v.url);
      return (
        <iframe
          src={`https://player.vimeo.com/video/${id}?autoplay=1`}
          title={title}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      );
    }
    return (
      <video
        src={v.url}
        poster={v.poster}
        controls
        autoPlay
        className="w-full h-full object-cover"
      />
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: idx * 0.05 }}
      className="relative rounded-2xl overflow-hidden bg-[#1A1A1A] border border-white/5 aspect-video"
      data-testid={`video-card-${v.id}`}
    >
      {playing ? (
        renderPlayer()
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          className="group absolute inset-0 w-full h-full"
          aria-label={`Play ${title}`}
        >
          {v.poster && (
            <img
              src={v.poster}
              alt={title}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#F37021] flex items-center justify-center group-hover:scale-110 transition-transform shadow-[0_16px_40px_rgba(243,112,33,0.5)]">
              <Play className="w-7 h-7 md:w-8 md:h-8 text-white fill-white ml-1" />
            </div>
          </div>
          {title && (
            <div className="absolute bottom-4 left-4 right-4 text-white font-display text-base md:text-lg font-semibold text-start">
              {title}
            </div>
          )}
        </button>
      )}
    </motion.div>
  );
}

export default function WorkVideos() {
  const { t, lang } = useLanguage();
  const V = t.videos;

  if (!videos || videos.length === 0) return null;

  return (
    <section
      id="videos"
      className="relative bg-[#111] text-white py-24 md:py-32 overflow-hidden"
      data-testid="videos-section"
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[350px] bg-[#F37021]/10 blur-[140px] rounded-full pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl mb-10 md:mb-14">
          <span className="inline-block text-xs font-semibold tracking-[0.3em] uppercase text-[#F37021] mb-4">
            — {V.eyebrow}
          </span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold leading-[1.08]">
            {V.title}
          </h2>
          <p className="mt-4 text-[#A3A3A3] text-base md:text-lg">{V.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {videos.map((v, i) => (
            <VideoCard key={v.id} v={v} idx={i} lang={lang} />
          ))}
        </div>
      </div>
    </section>
  );
}
